import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { MapPin, ChevronDown, ChevronUp } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

interface Story {
  id: number;
  title: string;
  excerpt: string;
  content: string;
  location: string;
  category: string;
  categoryColor: string;
}

const stories: Story[] = [
  {
    id: 1,
    title: 'Why We Started',
    excerpt: 'A Boston apartment, a lot of stress, and a bottle of lavender oil that changed everything.',
    content: `It started with a bad month.\n\nA new job, a tiny apartment in Dorchester, and a commute that left me arriving home feeling like I'd been wrung out. I'd tried everything — meditation apps, journaling, gym memberships I never used. Nothing stuck.\n\nThen a friend gave me a reed diffuser she'd made herself. Lavender and vetiver. Within 20 minutes of putting it on my bookshelf, something shifted. The apartment smelled different. I slowed down.\n\nI started researching the science. Lavender genuinely lowers cortisol levels. Vetiver is grounding — they call it the "oil of tranquility" in aromatherapy. Rosemary improves memory retention. These aren't wellness myths. The research is real.\n\nNestBloom grew out of that realization: you don't need a spa, a supplement stack, or a whole wellness routine. You need something that works in the space you actually live in — your apartment. That's what we make.`,
    location: 'Dorchester, Boston',
    category: 'Origin',
    categoryColor: '#4A7C59',
  },
  {
    id: 2,
    title: 'Boston-Made for City Living',
    excerpt: 'How a Boston kitchen table became a small-batch fragrance studio — and why that matters.',
    content: `Every bottle of NestBloom starts at a kitchen table in Boston, MA.\n\nWe work in small batches of 50–200 units at a time. That's intentional. It means we can adjust recipes between batches, test new blends on real apartments before launching them, and actually account for every unit we put out.\n\nOur essential oils come from Mountain Rose Herbs — certified organic, sourced responsibly. Our glass is amber and clear — not plastic, not huge. Our labels are printed locally. CandleChem in Brockton is a 20-minute drive for our wax and reeds.\n\nWe keep everything close because we believe "local" isn't just a marketing word. When something is made near you, by someone who lives near you, it's made differently. With more care. More accountability. More pride.\n\nBoston sits at the edge of the Blue Hills Reservation — actual nature, 10 minutes from downtown Boston. That context shapes what we make. You can feel the difference.`,
    location: 'Boston, MA',
    category: 'Behind the Brand',
    categoryColor: '#C4882A',
  },
];

const Stories: React.FC = () => {
  const { t } = useLanguage();
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const toggle = (id: number) => {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const renderContent = (story: Story, isExpanded: boolean) => {
    if (!isExpanded) {
      return (
        <p className="text-sm text-charcoal/70 leading-relaxed">
          {story.content.substring(0, 160)}…
        </p>
      );
    }
    return story.content.split('\n\n').filter(p => p.trim()).map((para, i) => (
      <p key={i} className="text-sm text-charcoal/70 leading-relaxed mb-3 last:mb-0">
        {para.trim()}
      </p>
    ));
  };

  return (
    <section id="stories" className="py-24 bg-white/60">
      <div className="container mx-auto px-6">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16 max-w-2xl mx-auto"
        >
          <span className="inline-block text-herb-green text-sm font-semibold tracking-widest uppercase mb-4">
            The Story
          </span>
          <h2 className="text-4xl md:text-5xl font-elegant font-bold text-forest mb-4">
            {t('stories.title')}
          </h2>
          <p className="text-warm-gray leading-relaxed">
            {t('stories.description')}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {stories.map((story, index) => (
            <motion.article
              key={story.id}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, delay: index * 0.15 }}
              className="bg-parchment/70 rounded-2xl overflow-hidden border border-linen hover:border-herb-light/40 hover:shadow-md transition-all duration-300"
            >
              {/* Accent bar */}
              <div className="h-1 w-full" style={{ backgroundColor: story.categoryColor }} />

              <div className="p-7">
                {/* Category + location */}
                <div className="flex items-center justify-between mb-4">
                  <span
                    className="text-xs font-semibold px-3 py-1 rounded-full text-white"
                    style={{ backgroundColor: story.categoryColor }}
                  >
                    {story.category}
                  </span>
                  <div className="flex items-center gap-1 text-warm-gray">
                    <MapPin className="w-3.5 h-3.5" />
                    <span className="text-xs">{story.location}</span>
                  </div>
                </div>

                {/* Title + excerpt */}
                <h3 className="text-xl font-elegant font-bold text-forest mb-2">
                  {story.title}
                </h3>
                <p className="text-sm text-warm-gray font-italic italic mb-4">
                  {story.excerpt}
                </p>

                {/* Content */}
                <div className="bg-white/60 rounded-xl p-4 mb-4">
                  {renderContent(story, expanded.has(story.id))}
                </div>

                {/* Toggle button */}
                <motion.button
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => toggle(story.id)}
                  className="flex items-center gap-1.5 text-sm font-medium text-herb-green hover:text-forest transition-colors"
                >
                  {expanded.has(story.id) ? (
                    <><ChevronUp className="w-4 h-4" /> {t('stories.readLess')}</>
                  ) : (
                    <><ChevronDown className="w-4 h-4" /> {t('stories.readMore')}</>
                  )}
                </motion.button>
              </div>
            </motion.article>
          ))}
        </div>

        {/* Closing quote */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="mt-16 text-center max-w-2xl mx-auto"
        >
          <div className="w-8 h-1 bg-herb-green rounded mx-auto mb-6" />
          <blockquote className="text-2xl md:text-3xl font-italic italic font-semibold text-forest mb-3">
            "{t('stories.quote.text')}"
          </blockquote>
          <p className="text-sm text-warm-gray">
            {t('stories.quote.source')}
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default Stories;
